import { faSliders } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import useFilters from "../../Hook/filters";
import { FilterDefinition, GENDER, ON_FEED, THERMOMETER, countMoreFilters, isActive, selectedOptions } from "./catalog";
import FilterOptionSheet from "./FilterOptionSheet";
import MoreFiltersSheet from "./MoreFiltersSheet";

/**
 * 15c. Two boxes and a chip.
 *
 * The box label is the filter name, always in the same place, and the value line is
 * the selection spelled out — never a count, never "+1". Nothing selected reads "All"
 * in muted text on an untinted box. A long selection ellipsises; the count appears
 * inside the sheet, not here.
 */
function FilterBox({ definition, onOpen }: { definition: FilterDefinition; onOpen: () => void }) {
  const intl = useIntl();
  const filters = useFilters();
  const value = filters.filters[definition.key];
  const active = isActive(definition, value);
  const chosen = selectedOptions(definition, value);

  return (
    <button
      type="button"
      onClick={onOpen}
      data-testid={`filter-box-${definition.key}`}
      className={
        "flex-1 min-w-0 rounded-panel px-3.5 py-2.5 text-left min-h-tap-min " +
        (active ? "bg-tint-box shadow-ring-box" : "bg-tint-neutral")
      }
    >
      <div className={"text-box-label " + (active ? "text-accent opacity-[var(--s4s-tint-box-label-opacity)]" : "text-muted")}>
        <FormattedMessage id={definition.title} />
      </div>
      <div className={"text-row font-medium truncate " + (active ? "text-accent" : "text-text")}>
        {active
          ? chosen.map((option) => intl.formatMessage({ id: option.label })).join(", ")
          : intl.formatMessage({ id: "filter__online_all" })}
      </div>
    </button>
  );
}

function FilterRow({ inPanel }: { inPanel?: boolean }) {
  const filters = useFilters();
  const [open, setOpen] = useState<FilterDefinition | "more" | null>(null);

  // The badge counts filters, not values, and only the five that are not on screen.
  const badge = countMoreFilters(filters.filters);

  // BC9: guests open every sheet — master let them keep the free "soft" tier,
  // and the gate moved to the locked options inside (FilterOptionSheet).
  const openFilter = (definition: FilterDefinition) => setOpen(definition);

  return (
    // Inside the tabs panel (35a) the panel already pads; standalone it insets itself.
    <div className={(inPanel ? "" : "px-page-x ") + "flex gap-2.5 items-stretch"}>
      {ON_FEED.map((definition) => (
        <FilterBox key={definition.key} definition={definition} onOpen={() => openFilter(definition)} />
      ))}

      <button
        type="button"
        onClick={() => setOpen("more")}
        data-testid="filter-more-chip"
        aria-label="More filters"
        className="relative w-chip flex-none rounded-panel bg-tint-neutral flex items-center justify-center text-muted"
      >
        <FontAwesomeIcon icon={faSliders} />
        {badge > 0 && (
          <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-pill bg-accent text-ink text-[10.5px] font-semibold leading-[18px] text-center">
            {badge}
          </span>
        )}
        <span className="sr-only"><FormattedMessage id="filter__more_title" /></span>
      </button>

      {open === "more" && <MoreFiltersSheet onClose={() => setOpen(null)} />}
      {open && open !== "more" && (
        <FilterOptionSheet
          definition={open}
          value={filters.filters[open.key]}
          onClose={() => setOpen(null)}
          onApply={(value) => {
            filters.setFilter(open.key, value);
            setOpen(null);
          }}
        />
      )}
    </div>
  );
}

export { FilterBox, GENDER, THERMOMETER };
export default FilterRow;
